import { useRef } from 'react';
import styled from 'styled-components';
import {
  addTrackingToExternalBookingUrl,
  getEventUrl,
  getLessonJoinPageUrl,
} from 'constants/pages';
import { trackEvent } from 'services/client/analytics';
import { trackCtaClick } from 'services/client/analytics/engagement';
import { CLINIC_JOIN_CLICKED } from 'services/client/analytics/events';
import { getActivityImageUrl } from 'utils/shared/getActivityImageUrl';
import { convertUnitPriceToFormattedPrice } from 'utils/shared/money/convertUnitPriceToFormattedPrice';
import { formatVenueEventDateTime } from 'utils/shared/time/formatEventDateTime';
import { resolveVenueTimeZone } from 'utils/shared/time/venueDayFilters';
import { getProfileImageUrlOrPlaceholder } from 'utils/shared/user/getProfileImageUrlOrPlaceholder';
import { useEngagementImpression } from 'hooks/useEngagementImpression';
import AlertIcon from 'svg/AlertIcon';
import CoachBadge from 'svg/CoachBadge';
import Directions from 'svg/Directions';
import Location from 'svg/Location';
import { getClinicLinkType } from 'screens/ExploreClinics/getClinicLinkType';
import { ClinicCardProps } from 'screens/ExploreClinics/props';
import ActivityBadge from 'components/ActivityBadge';
import { ButtonLink } from 'components/Button';
import classNames from 'styles/utils/classNames';

// this is to remove previous card's border if current card is hovered to prevent overlap
const ClinicItem = styled.div`
  &:has(+ div:hover) {
    border-color: transparent;
  }
`;
const ClinicCard: React.FC<ClinicCardProps> = ({
  clinic,
  listHoveredClinicId,
  setListHoveredClinicId,
  setListHoveredVenueId,
  onClinicDetail,
  isHidePhoto = false,
  impressionSource = 'BROWSE',
  placementId = null,
}) => {
  const rfxEvent = clinic.rfxEvent;
  // Engagement: a clinic is a LESSON entity (an RFX class an EVENT). Surfaced from the
  // clinics marketplace, so impressions/clicks are attributed to the card's source
  // directly (BROWSE for the regular list, BOOST for a featured placement).
  const entityType = rfxEvent ? 'EVENT' : 'LESSON';
  const cardRef = useRef<HTMLDivElement>(null);
  useEngagementImpression(cardRef, {
    entityType,
    entityId: clinic.id,
    source: impressionSource,
    placementId,
  });

  const handleJoinClick = (e: React.MouseEvent, source: 'card' | 'modal' = 'card') => {
    e.stopPropagation();

    trackCtaClick({
      entityType,
      entityId: clinic.id,
      source: impressionSource,
      placementId,
    });

    // Track clinic join button click event
    const linkType = getClinicLinkType(clinic);
    trackEvent(CLINIC_JOIN_CLICKED, {
      clinic_id: clinic.id,
      clinic_title: clinic.title,
      link_type: linkType,
      source: source,
      sport: clinic.sport,
      external_source: clinic.externalSource || null,
      has_external_booking_url: !!clinic.externalBookingUrl?.trim(),
      price_unit_amount: clinic.priceUnitAmount,
      participant_count: clinic.participants?.length || 0,
      participant_limit: clinic.participantLimit || 0,
      venue_name: clinic.venue?.title || clinic.userCustomCourt?.fullAddress || null,
      coach_name: clinic.ownerProfile?.fullName || null,
      start_date_time: clinic.startDateTime,
    });
  };
  const startDateTime = clinic?.startDateTime;
  const venueTz = resolveVenueTimeZone(clinic);
  const lessonDateTime = formatVenueEventDateTime(startDateTime, clinic?.endDateTime, venueTz);
  const lessonDate = lessonDateTime ? `${lessonDateTime.day} ${lessonDateTime.date}` : '';
  const lessonStartTime = lessonDateTime?.startTime ?? '';
  const lessonEndTime = lessonDateTime?.endTime ?? '';
  const lessonTzLabel = lessonDateTime?.timezoneLabel ?? '';
  // No limit means unlimited: no "spots left" count and Join always offered.
  const participantLimit = clinic?.participantLimit || null;
  const participantCount = clinic?.participants.length;
  const spotsLeft =
    participantLimit === null ? null : Math.max(0, participantLimit - participantCount);
  const isExternal = getClinicLinkType(clinic) === 'external';

  const price = clinic.priceUnitAmount
    ? convertUnitPriceToFormattedPrice(clinic.priceUnitAmount, clinic.currency).priceDisplay
    : 0;
  const joinHref = rfxEvent ? getEventUrl(rfxEvent.slug) : getLessonJoinPageUrl(clinic.id);
  const packSchedule =
    rfxEvent && rfxEvent.sessionCount > 1
      ? [rfxEvent.cadence, `${rfxEvent.sessionCount} sessions`].filter(Boolean).join(' · ')
      : null;

  const renderLocation = (
    <div className="mt-ds-md flex items-center gap-4 max-sm:mt-ds-xs">
      <div className="inline-flex gap-1 text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
        <Location className="h-4 w-4 shrink-0 text-color-text-lightmode-icon dark:text-color-text-darkmode-icon" />
        <span className="typography-product-text-card-desktop max-sm:typography-product-text-card-mobile max-sm:line-clamp-1">
          {clinic?.userCustomCourt?.fullAddress || clinic?.venue?.addressString}
        </span>
      </div>
      <a
        target="_blank"
        href={`https://maps.google.com?q=${encodeURIComponent(
          clinic?.userCustomCourt?.fullAddress || clinic?.venue?.addressString || '',
        )}`}
        className="inline-flex items-center gap-1 max-sm:hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <Directions className="mb-[0.9px] h-[0.87rem] w-[0.87rem] shrink-0" />
        <span className="typography-product-button-label-small">Directions</span>
      </a>
    </div>
  );

  const renderParticipants = (
    <div className="isolate flex">
      {[...clinic?.participants].map((participant, index) => (
        <Participant
          index={index}
          key={participant.id}
          imagePath={getProfileImageUrlOrPlaceholder({
            path: participant.userProfile?.profileImagePath || '',
          })}
        />
      ))}
    </div>
  );

  const coverSrc = getActivityImageUrl(clinic) || '';

  return (
    <ClinicItem
      ref={cardRef}
      className={classNames(
        'flex min-w-0 cursor-pointer gap-4 py-5 transition-all duration-200',
        'px-0 sm:px-5',
        'border-b border-color-border-input-lightmode dark:border-color-border-input-darkmode',
        'border-l-transparent border-r-transparent border-t-transparent',
        'border hover:rounded-2xl sm:hover:border-color-border-hover-lightmode sm:hover:dark:border-color-border-hover-darkmode',
        listHoveredClinicId === clinic.id &&
          'rounded-2xl !border-color-border-hover-lightmode dark:!border-color-border-hover-darkmode',
      )}
      onMouseEnter={() => {
        setListHoveredClinicId(clinic.id);
        if (clinic.venue?.id) setListHoveredVenueId?.(clinic.venue.id);
      }}
      onMouseLeave={() => {
        setListHoveredClinicId(null);
        setListHoveredVenueId?.(null);
      }}
      onClick={() => onClinicDetail(clinic)}
    >
      {!isHidePhoto && (
        <img
          src={coverSrc}
          alt="clinic-owner"
          className="max-h-[12.5rem] w-full max-w-[12.5rem] rounded-xl border border-color-border-input-lightmode bg-gray-100 object-cover object-center dark:border-color-border-input-darkmode max-sm:max-h-[7.5rem] max-sm:max-w-[7.5rem]"
        />
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex justify-between gap-2">
          <div className="flex items-start gap-2">
            <span className="max-sm:typography-product-chips-filters typography-product-element-label text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
              {lessonDate} <span className="text-color-brand-primary">&bull;</span>{' '}
              {lessonStartTime}
              {lessonEndTime ? ` - ${lessonEndTime}` : ''}
              {lessonTzLabel ? ` ${lessonTzLabel}` : ''}
            </span>
            {rfxEvent && <ActivityBadge type={clinic.type} sport={clinic.sport} isRfx size="xs" />}
          </div>
          {!!price && <span className="typography-product-subheading">{price}</span>}
        </div>
        <span className="typography-product-card-title-desktop max-sm:typography-product-card-title-mobile mt-ds-xs sm:mt-ds-sm">
          {clinic.title}
        </span>
        {packSchedule && (
          <span className="typography-product-text-card-desktop max-sm:typography-product-text-card-mobile mt-ds-xs text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
            {packSchedule}
          </span>
        )}
        <span className="typography-product-text-card-desktop mt-ds-sm line-clamp-3 break-words max-sm:hidden">
          {clinic.description}
        </span>
        {renderLocation}
        <span className="typography-product-text-card-desktop max-sm:typography-product-text-card-mobile mt-ds-sm flex items-center gap-1 text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary max-sm:mt-ds-xs">
          <CoachBadge className="h-4 w-4 text-color-text-lightmode-icon dark:text-color-text-darkmode-icon" />
          {isExternal
            ? clinic.venue?.title ||
              clinic.ownerProfile?.fullName ||
              clinic.venue?.addressString ||
              'Venue'
            : clinic.ownerProfile?.fullName}
        </span>
        <div className="typography-product-text-card-desktop max-sm:typography-product-text-card-mobile mt-auto flex min-w-0 items-center justify-between  text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
          {!clinic.externalBookingUrl ? (
            <div className="flex items-center gap-ds-sm">
              <span>Players Going</span>
              <span>
                {participantCount}
                {participantLimit !== null && `/${participantLimit}`}
              </span>
              {renderParticipants}
            </div>
          ) : (
            <div>&nbsp;</div>
          )}
          <div className="flex  min-w-fit items-center gap-ds-sm">
            {!clinic.externalBookingUrl && spotsLeft !== null ? (
              <span
                className={classNames(
                  'flex shrink-0 gap-1',
                  spotsLeft === 0 && 'text-color-brand-primary',
                )}
              >
                {spotsLeft === 0 && <AlertIcon className="h-4 w-4" viewBox="0 0 23 21" />}
                {spotsLeft} spots left
              </span>
            ) : (
              <div>&nbsp;</div>
            )}
            {!!clinic.externalBookingUrl?.trim() && (
              <ButtonLink
                variant="brand"
                size="sm"
                className="w-[6.25rem] max-sm:hidden"
                isExternal
                href={addTrackingToExternalBookingUrl(clinic.externalBookingUrl, clinic.id)}
                onClick={(e) => handleJoinClick(e, 'card')}
              >
                Join
              </ButtonLink>
            )}
            {spotsLeft !== 0 && !clinic.externalBookingUrl && (
              <ButtonLink
                variant="brand"
                size="sm"
                className="w-[6.25rem] max-sm:hidden"
                href={joinHref}
                onClick={(e) => handleJoinClick(e, 'card')}
              >
                Join
              </ButtonLink>
            )}
          </div>
        </div>
      </div>
    </ClinicItem>
  );
};

const Participant: React.FC<{ imagePath: string; index: number }> = ({ imagePath, index }) => (
  <div style={{ translate: index > 0 ? -(8 * index) : 0 }}>
    <img src={imagePath} alt="avatar" className="relative h-6 w-6 rounded-full" />
  </div>
);

export default ClinicCard;
